/**
 * The device this browser reports to `POST /users/me/session`.
 *
 * A localStorage UUID, chosen at intake over a fingerprint: a fingerprint is
 * both more identifying than we need and less stable than it looks. The cost is
 * that clearing site data looks like a new device and earns a security email,
 * which is the correct failure direction — a false alarm beats a missed one.
 *
 * The backend treats the header as optional (`api/v1/endpoints/users.py:45`
 * puts the whole device-tracking task behind `if device_id:`), so a browser
 * that refuses storage still signs in. It simply is not tracked. That contract
 * is the rule the whole module answers to: no condition a supported browser can
 * present may throw here, because from Task 5 this runs on the session request
 * itself, and a failure to identify the device must never become a failure to
 * sign in. Blocked storage and a missing `randomUUID` are both such conditions
 * and are both handled below. A total absence of Web Crypto is not — see
 * `mint()`.
 */
export const DEVICE_ID_KEY = 'tracke5.device.id'

/**
 * Resolved once per page load.
 *
 * Not an optimisation. `getDeviceId()` is read per request, and without this a
 * `localStorage.clear()` between two calls would report two devices inside one
 * session — the user emailed a new-device alert about the tab they are sitting
 * in. One page load is one device; a reload is where a new id may appear.
 */
let memo: string | null = null

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

/**
 * The stored value is outside our control — a hand-edited devtools entry, a key
 * from an older scheme, anything else on the origin. Two of those round-trip
 * into real damage: `''` is falsy to the backend's `if device_id:`, so the
 * browser reports itself untracked forever, and a value containing CR/LF makes
 * `headers.set('X-Device-Id', …)` throw and takes the request with it. Mint
 * over anything unrecognised rather than propagating it, as ThemeProvider.tsx:42
 * does with its own stored value.
 */
function isDeviceId(value: string | null): value is string {
  return value !== null && UUID.test(value)
}

/** Storage that never throws. Private mode and blocked cookies both do. */
function read(): string | null {
  try {
    return localStorage.getItem(DEVICE_ID_KEY)
  } catch {
    return null
  }
}

function write(value: string): void {
  try {
    localStorage.setItem(DEVICE_ID_KEY, value)
  } catch {
    // Deliberately swallowed — see the module comment. The memo above still
    // holds the id for this page load, so the session stays internally
    // consistent; only persistence across reloads is lost.
  }
}

/**
 * `crypto.randomUUID` is secure-context-only, and absent before Safari 15.4.
 * `https://` and `localhost` both qualify, so the realistic gap is a plain-http
 * origin — `vite --host` opened from a phone to check the mobile hit targets —
 * where `crypto` exists but the method does not. Calling it unguarded there
 * throws, which would cost the sign-in rather than the tracking.
 *
 * `getRandomValues` has no such restriction, so composing v4 by hand covers the
 * gap. A platform missing that too is not one this app runs on at all.
 */
function mint(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()

  const hex = Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte, index) => {
    // Version 4 in the high nibble of byte 6, variant 10xx in byte 8.
    const value = index === 6 ? (byte & 0x0f) | 0x40 : index === 8 ? (byte & 0x3f) | 0x80 : byte
    return value.toString(16).padStart(2, '0')
  }).join('')

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export function getDeviceId(): string {
  if (memo !== null) return memo
  const stored = read()
  if (isDeviceId(stored)) {
    memo = stored
    return memo
  }
  memo = mint()
  write(memo)
  return memo
}

/** Test seam. Simulates a page reload, which is the only thing that clears the memo. */
export function __resetDeviceId(): void {
  memo = null
}
